// Basic
import PropTypes from 'prop-types';
// Libs
import { jsPDF } from 'jspdf';
import { useTranslation } from 'react-i18next';
// Components
import Entity from '../../../../../../common-utils/components/renders/entity/Entity';
// Utils
import previewLanguage from '../../../../../../common-utils/functions/previewLanguage';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
// Images
import logoOrgaUrl from 'url:../../../../../../../resource/images/preludd_white.png';
import logoAppUrl from 'url:../../../../../../../resource/images/payludd.png';
import noticeENUrl from 'url:../../../../../../../resource/images/notice-EN.png';
import noticeFRUrl from 'url:../../../../../../../resource/images/notice-FR.png';
import supportENUrl from 'url:../../../../../../../resource/images/support-EN.png';
import supportFRUrl from 'url:../../../../../../../resource/images/support-FR.png';

/**
 * @param root0
 * @param root0.customization
 * @param root0.merchantAccountLabel
 * @param root0.qrCodeElementId
 * @returns {JSX} PersoPDFFile
 */
function PersoPDFFile({ customization, merchantAccountLabel, qrCodeElementId = 'qr-code-svg' }) {
    const { t: translate, i18n } = useTranslation(nsCustomization);
    const langBomo = i18n.language?.substring(0, 2) || 'fr';

    // Load image and convert to base64 via canvas
    const loadImageAsBase64 = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/jpeg', 0.95));
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    };

    const handleDownload = async () => {
        if (!customization?.packageCode) {
            return;
        }

        // Load all images first (notice/support use UI language, not customization language)
        const logoOrgaBase64 = await loadImageAsBase64(logoOrgaUrl);
        const logoAppBase64 = await loadImageAsBase64(logoAppUrl);
        const noticeBase64 = await loadImageAsBase64(langBomo === 'en' ? noticeENUrl : noticeFRUrl);
        const supportBase64 = await loadImageAsBase64(langBomo === 'en' ? supportENUrl : supportFRUrl);

        // Create PDF
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        // Title
        doc.setFontSize(26);
        doc.setFont('Helvetica', 'Bold');
        doc.text(translate('modal.qr_code.pdf.title'), pageWidth / 2, 40, { align: 'center' });

        // Subtitle
        doc.setFontSize(14.5);
        doc.setFont('Helvetica', 'Bold');
        doc.text(translate('modal.qr_code.pdf.subtitle'), pageWidth / 2, 75, { align: 'center' });

        // Get QR code as data URL
        const qrCodeElement = document.getElementById(qrCodeElementId);
        if (qrCodeElement) {
            const svgData = new XMLSerializer().serializeToString(qrCodeElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                const qrCodeDataUrl = canvas.toDataURL('image/png');

                // Add logos if loaded
                if (logoOrgaBase64) {
                    doc.addImage(logoOrgaBase64, 'JPEG', 5, 20, 40, 30);
                }
                if (logoAppBase64) {
                    doc.addImage(logoAppBase64, 'JPEG', 175, 25, 20, 20);
                }

                // Add QR code to PDF (centered, size 50x50)
                doc.addImage(qrCodeDataUrl, 'PNG', 80, 92, 50, 50);

                // Add notice and support images at bottom
                if (noticeBase64) {
                    doc.addImage(noticeBase64, 'JPEG', 3, 175.5, 70, 90);
                }
                if (supportBase64) {
                    doc.addImage(supportBase64, 'JPEG', 137, 176, 70, 90);
                }

                // Orange "Information" rectangle in center-bottom
                const rectWidth = 60;
                const rectHeight = 16;
                const yStart = 176.3;
                const totalRectWidth = 205;
                const numRects = 3;
                const spacing = (totalRectWidth - (rectWidth * numRects)) / (numRects - 1);
                const rectX = (pageWidth - totalRectWidth) / 2 + rectWidth + spacing;

                const infoText = translate('modal.qr_code.pdf.information');
                doc.setFillColor(235, 93, 47);
                doc.roundedRect(rectX, yStart, rectWidth, rectHeight, 3, 3, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(17);
                doc.text(infoText, rectX + rectWidth / 2, yStart + rectHeight / 2 + 2, { align: 'center' });
                const textLength = doc.getTextDimensions(infoText);
                doc.setDrawColor(255, 255, 255);
                doc.line((pageWidth + textLength.w) / 2, yStart + rectHeight / 2 + 3, (pageWidth - textLength.w) / 2, yStart + rectHeight / 2 + 3);
                doc.setDrawColor(0);
                doc.setTextColor(0, 0, 0);

                // Package code
                doc.setFontSize(18);
                doc.setFont('Helvetica', 'bold');
                const formattedPackageCode = customization.packageCode.replace(/(\d{3})(\d{3})(\d{3})/, '$1 - $2 - $3');
                doc.text(formattedPackageCode, pageWidth / 2, 160, { align: 'center' });

                // Information section
                doc.setFontSize(13);
                doc.setTextColor(84, 84, 84);
                const yBlockStart = 205;
                const step = 6;
                const texts = [
                    translate('modal.qr_code.pdf.merchant_account'),
                    merchantAccountLabel || 'N/A',
                    translate('modal.qr_code.pdf.initialization_code'),
                    formattedPackageCode,
                    translate('modal.qr_code.pdf.customization_name'),
                    customization.packageLabel || 'N/A',
                    translate('modal.qr_code.pdf.color_code'),
                    customization.color || 'N/A',
                    translate('modal.qr_code.pdf.selected_language'),
                    previewLanguage(customization.locale)[3] || 'N/A'
                ];

                for (let i = 0; i < texts.length; i++) {
                    const yPos = yBlockStart + i * step;
                    // Labels (even index) are normal, values (odd index) are bold
                    doc.setFont('Helvetica', i % 2 === 0 ? 'normal' : 'Bold');
                    doc.text(texts[i], pageWidth / 2, yPos, { align: 'center' });
                }

                // Save PDF
                const fileName = `${customization.packageLabel || 'customization'}_${formattedPackageCode}.pdf`;
                doc.save(fileName);
            };

            img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
        }
    };

    return (
        <div className='text-center'>
            <button
                onClick={handleDownload}
                className='text-blue-600 hover:text-blue-800 underline cursor-pointer'
                disabled={!customization?.packageCode}
            >
                <Entity entity='modal.qr_code.download' namespace={nsCustomization} />
            </button>
        </div>
    );
}

PersoPDFFile.propTypes = {
    customization: PropTypes.object.isRequired,
    merchantAccountLabel: PropTypes.string,
    qrCodeElementId: PropTypes.string
};

export default PersoPDFFile;
